import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../config/api';

const DEFAULT_GEO_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 30000,
  updateInterval: 15000,
};

// Throttle interval for server location updates (ms)
const SERVER_UPDATE_INTERVAL = 15000;

const useGeolocation = (options = DEFAULT_GEO_OPTIONS) => {
  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    accuracy: null,
    timestamp: null
  });
  const [error, setError] = useState(null);
  const [isWatching, setIsWatching] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 5;
  const lastServerUpdateRef = useRef(0);

  // Use a ref to keep options stable across renders
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const updateServerLocation = useCallback(async (latitude, longitude) => {
    // Throttle server updates to prevent 429 errors
    const now = Date.now();
    if (now - lastServerUpdateRef.current < SERVER_UPDATE_INTERVAL) {
      return;
    }
    lastServerUpdateRef.current = now;
    try {
      await api.put('/user/location', { latitude, longitude });
    } catch (err) {
      // Silently ignore 429 errors, only log others
      if (!err.response || err.response.status !== 429) {
        console.error('Failed to update location on server:', err);
      }
    }
  }, []);

  const handleSuccess = useCallback((position) => {
    const { latitude, longitude, accuracy } = position.coords;
    const timestamp = new Date(position.timestamp);
    
    setLocation({ latitude, longitude, accuracy, timestamp });
    setError(null);
    setRetryCount(0);

    // Update server with new location (throttled)
    updateServerLocation(latitude, longitude);
  }, [updateServerLocation]);

  const handleError = useCallback((err) => {
    const shouldRetry = retryCount < maxRetries;
    
    let errorMessage = 'Location access failed';
    
    switch (err.code) {
      case err.PERMISSION_DENIED:
        errorMessage = 'Location access denied. Please enable location services and refresh the page.';
        break;
      case err.POSITION_UNAVAILABLE:
        errorMessage = shouldRetry
          ? `Position unavailable, retrying... (${retryCount + 1}/${maxRetries})`
          : 'Location information is unavailable. Please try again later.';
        break;
      case err.TIMEOUT:
        errorMessage = shouldRetry
          ? `Location request timed out, retrying... (${retryCount + 1}/${maxRetries})`
          : 'Location request timed out. Please try again.';
        break;
      default:
        errorMessage = `Unknown location error: ${err.message}`;
        break;
    }
    
    setError(errorMessage);
    
    if (shouldRetry) {
      setRetryCount(prev => prev + 1);
      setTimeout(() => {
        const retryOptions = {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 300000,
        };
        navigator.geolocation.getCurrentPosition(handleSuccess, handleError, retryOptions);
      }, 1000);
    }
  }, [retryCount, maxRetries, handleSuccess]);

  const startWatching = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser');
      return;
    }

    if (isWatching) {
      return;
    }

    setIsWatching(true);
    setError(null);
    setRetryCount(0);

    const geoOptions = optionsRef.current;

    // Get initial position
    navigator.geolocation.getCurrentPosition(handleSuccess, handleError, geoOptions);

    // Start watching position
    const id = navigator.geolocation.watchPosition(handleSuccess, handleError, geoOptions);
    setWatchId(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWatching, handleSuccess, handleError]);

  const stopWatching = useCallback(() => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
    setIsWatching(false);
    setRetryCount(0);
  }, [watchId]);

  const getCurrentPosition = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser');
      return Promise.reject(new Error('Geolocation not supported'));
    }

    setRetryCount(0);
    const geoOptions = optionsRef.current;
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          handleSuccess(position);
          resolve(position);
        },
        (err) => {
          handleError(err);
          reject(err);
        },
        geoOptions
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSuccess, handleError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [watchId]);

  return {
    location,
    error,
    isWatching,
    startWatching,
    stopWatching,
    getCurrentPosition,
    retryCount
  };
};

export default useGeolocation;
